/**
 * ClaruStatsPanel
 * Coláiste Lurgan Management System - insight into students received via the Claru deposit webhook
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { Users, UserCheck, Clock, RefreshCw, Loader2, BookOpen, ArrowRight } from 'lucide-react';

export function ClaruStatsPanel() {
  const { t } = useTranslation();
  const { data: stats, isLoading, refetch } = useQuery({
    queryKey: ['claru-stats'],
    queryFn: api.students.claruStats,
    staleTime: 30 * 1000,
  });

  const lastReceivedLabel = stats?.last_received_at
    ? new Date(stats.last_received_at).toLocaleString('en-IE')
    : t('settings.claru.stats.none', 'None yet');

  const statCards = [
    {
      label: t('settings.claru.stats.total', 'Students Received'),
      value: stats?.total ?? '—',
      icon: <Users className="h-5 w-5 text-emerald-500" />,
    },
    {
      label: t('settings.claru.stats.pendingReview', 'Pending Review'),
      value: stats?.pending_review ?? '—',
      icon: <UserCheck className={`h-5 w-5 ${stats?.pending_review > 0 ? 'text-amber-500' : 'text-muted-foreground'}`} />,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('settings.claru.title', 'Claru Integration')}</CardTitle>
            <CardDescription>
              {t('settings.claru.description', 'Insight into student deposits received from Claru — Bainisteoir creates the student and parent account automatically on arrival.')}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            {t('common.refresh', 'Refresh')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4">
          {statCards.map((card) => (
            <div key={card.label} className="flex flex-col items-center gap-2 p-4 rounded-lg border bg-card text-center">
              {card.icon}
              <span className="text-2xl font-bold">
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : card.value}
              </span>
              <span className="text-xs text-muted-foreground">{card.label}</span>
            </div>
          ))}
        </div>

        {/* Last received */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>{t('settings.claru.stats.lastReceived', 'Last received')}:</span>
          <span className="font-medium text-foreground">{isLoading ? '—' : lastReceivedLabel}</span>
        </div>

        {/* Breakdown by course */}
        <div>
          <h4 className="flex items-center gap-1.5 text-sm font-medium mb-2">
            <BookOpen className="h-4 w-4" />
            {t('settings.claru.stats.byCourse', 'By course')}
          </h4>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading', 'Loading...')}
            </div>
          ) : stats?.by_course?.length > 0 ? (
            <div className="space-y-1.5">
              {stats.by_course.map((row) => (
                <div key={row.course_name} className="flex items-center justify-between text-sm py-1.5 px-3 rounded-md border bg-card">
                  <span>{row.course_name}</span>
                  <Badge variant="secondary">{row.count}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('settings.claru.stats.noneYet', 'No students received via Claru yet.')}
            </p>
          )}
        </div>

        {stats?.pending_review > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {t('settings.claru.stats.pendingReviewHint', '{{count}} returning-student match(es) need staff review.', { count: stats.pending_review })}
            </p>
            <Button asChild size="sm" variant="outline" className="shrink-0 gap-1.5">
              <Link to="/students/identity-reviews">
                {t('settings.claru.stats.reviewNow', 'Review Now')}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        )}

      </CardContent>
    </Card>
  );
}

export default ClaruStatsPanel;
